import { RadioGroup as HeadlessUIRadioGroup } from "@headlessui/react";
import type { ReactNode } from "react";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";

import FormInput from "~/components/formElements/FormInput";
import Label from "~/components/formElements/Label";

import type { RadioGroupVariantProps} from "./radioGroupVariants";
import { radioGroupVariants } from "./radioGroupVariants";

type RadioGroupValue = string | number | boolean;

interface RadioGroupOption<TValue extends RadioGroupValue = RadioGroupValue> {
	value: TValue;
	label?: string;
	element?: ReactNode;
	disabled?: boolean;
}

interface RadioGroupPlainProps<TValue extends RadioGroupValue = RadioGroupValue> {
	label?: string;
	name: string;
	options: RadioGroupOption<TValue>[];
	value: TValue;
	onChange: (value: TValue) => void;
	numCols?: 2 | 3 | 4 | 5;
	inputSize?: RadioGroupVariantProps["size"];
}

export function RadioGroupPlain<TValue extends RadioGroupValue>({
	label,
	options,
	name = "",
	value,
	onChange,
	numCols = 4,
	inputSize
}: RadioGroupPlainProps<TValue>) {
	const { grid, option, label: labelSlot } = radioGroupVariants({ numCols, interactive: true, size: inputSize });

	return (
		<FormInput>
			{label && <Label>{label}</Label>}
			<HeadlessUIRadioGroup value={value !== undefined ? value : undefined}
								  onChange={onChange}
								  name={name}>
				<div className={grid()}>
					{options.map(({ label: optLabel, element, value: optValue, disabled }, optIndex) => (
						<HeadlessUIRadioGroup.Option
							key={optIndex}
							value={optValue}
							className={({ checked }) => option({ checked })}
							disabled={disabled}
						>
							<HeadlessUIRadioGroup.Label as="span"
														className={labelSlot()}>
								{element || optLabel || optValue}
							</HeadlessUIRadioGroup.Label>
						</HeadlessUIRadioGroup.Option>
					))}
				</div>
			</HeadlessUIRadioGroup>
		</FormInput>
	);
}

interface RadioGroupDisplayProps<TValue extends RadioGroupValue = RadioGroupValue> {
	label?: string;
	options: RadioGroupOption<TValue>[];
	value: TValue | null;
	numCols?: 2 | 3 | 4 | 5;
}

export function RadioGroupDisplay<TValue extends RadioGroupValue>({
	label,
	options,
	value,
	numCols = 4,
}: RadioGroupDisplayProps<TValue>) {
	const { grid, option, label: labelSlot } = radioGroupVariants({ numCols, interactive: false });

	return (
		<FormInput>
			{label && <Label>{label}</Label>}
			<div className={grid()}>
				{options.map(({ label: optLabel, element, value: optValue }, optIndex) => {
					const checked = optValue === value;
					return (
						<div key={optIndex}
							 className={option({ checked })}>
							<span className={labelSlot()}>
								{element || optLabel || optValue}
							</span>
						</div>
					);
				})}
			</div>
		</FormInput>
	);
}

interface Props<TValue extends RadioGroupValue = RadioGroupValue> {
	label?: string;
	name: string;
	options: RadioGroupOption<TValue>[];
	control: Control<any>;
	numCols?: 2 | 3 | 4 | 5;
	inputSize?: RadioGroupVariantProps["size"];
}

function RadioGroup<TValue extends RadioGroupValue>({
	label,
	options,
	control,
	name = "",
	numCols = 4,
	inputSize
}: Props<TValue>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { onChange, value } }) => (
				<RadioGroupPlain
					label={label}
					options={options}
					name={name}
					value={value}
					onChange={onChange}
					numCols={numCols}
					inputSize={inputSize}
				/>
			)}
		/>
	);
}

export default RadioGroup;